import { AnimatedLogo } from "@/components/animated-logo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { useForm } from "@/components/ui/form";
import { Input, InputElement } from "@/components/ui/input";
import type { PlainModalComponentProps } from "@/components/ui/modal";
import { Separator } from "@/components/ui/separator";
import { signupSchema } from "@/libs/auth/schema";
import * as m from "@/libs/i18n/messages";
import { useRequestOtpMutation } from "@/services/otp.query";
import { OtpScene } from "@/services/otp.schema";
import { queries } from "@/utils/query";
import { useStore } from "@tanstack/react-form";
import { useNavigate } from "@tanstack/react-router";
import { useCountDown } from "ahooks";
import { KeyIcon, LockIcon, MailIcon, UserIcon } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import { z } from "zod";

export function SignupFormCard({
  closeModal,
}: Partial<PlainModalComponentProps>) {
  const navigate = useNavigate();

  const signupMutation = queries.auth.signup.useMutation({
    onSuccess() {
      toast.success("注册成功，请重新登录...");
      navigate({ to: "/auth/login" });
    },
  });

  const form = useForm(
    signupSchema.extend({
      consent: z.boolean().refine((value) => value === true),
    }),
    {
      defaultValues: {
        username: "",
        email: "",
        password: "",
        otp: "",
        consent: false,
      },
      onSubmit: async ({ value }) => {
        await signupMutation.mutateAsync(value);
        if (closeModal) {
          closeModal();
        }
      },
    },
  );

  const emailValid = useStore(
    form.store,
    (formState) =>
      formState.fieldMeta.email?.isTouched &&
      formState.fieldMeta.email?.errors.length === 0,
  );

  const [otpRequestCooldownTarget, setOtpRequestCooldownTarget] =
    useState<number>();
  const [otpRequestCooldown] = useCountDown({
    targetDate: otpRequestCooldownTarget,
  });
  const requestOtpMutation = useRequestOtpMutation({
    onSuccess: () => {
      setOtpRequestCooldownTarget(Date.now() + 60 * 1000);
    },
  });
  const allowRequestOtp = emailValid && otpRequestCooldown === 0;

  return (
    <Card className="w-full px-8 py-16 mx-auto xl:max-w-lg 2xl:max-w-xl sm:px-16">
      <CardHeader className="p-0">
        <AnimatedLogo className="block w-auto h-24 mx-auto mb-8 sm:mb-16" />
        <CardTitle className="text-3xl font-normal text-center">
          {m.ok_loud_spider_emerge()}
        </CardTitle>
      </CardHeader>
      <Separator className="my-4 sm:my-6" />
      <CardContent className="p-0">
        <form.Root className="space-y-4">
          <form.Field
            name="username"
            render={(field) => (
              <field.Container
                label={m.petty_major_clownfish_amuse()}
                disableController
              >
                <div className="relative">
                  <InputElement>
                    <UserIcon />
                  </InputElement>
                  <field.Controller>
                    <Input placeholder={m.petty_major_clownfish_amuse()} />
                  </field.Controller>
                </div>
              </field.Container>
            )}
          />

          <form.Field
            name="email"
            render={(field) => (
              <field.Container
                label={m.broad_fluffy_puma_delight()}
                disableController
              >
                <div className="relative">
                  <InputElement>
                    <MailIcon />
                  </InputElement>
                  <field.Controller>
                    <Input placeholder={m.broad_fluffy_puma_delight()} />
                  </field.Controller>
                </div>
              </field.Container>
            )}
          />

          <form.Field
            name="otp"
            render={(field) => (
              <field.Container
                label={m.heroic_glad_shark_sprout()}
                disableController
              >
                <div className="relative">
                  <InputElement>
                    <KeyIcon />
                  </InputElement>
                  <InputElement side="end">
                    <Button
                      data-slot="suffix"
                      type="button"
                      className="px-0"
                      disabled={!allowRequestOtp}
                      intent="link"
                      size="md"
                      onClick={() => {
                        requestOtpMutation.mutate({
                          scene: OtpScene.VerifyMail,
                          context: {
                            mail: field.form.getFieldValue("email"),
                          },
                        });
                      }}
                    >
                      {otpRequestCooldown === 0
                        ? m.bright_petty_marlin_soar()
                        : `${m.top_inclusive_trout_tickle()} (${Math.round(otpRequestCooldown / 1000)}s)`}
                    </Button>
                  </InputElement>
                  <field.Controller>
                    <Input
                      type="text"
                      placeholder={m.heroic_glad_shark_sprout()}
                    />
                  </field.Controller>
                </div>
              </field.Container>
            )}
          />

          <form.Field
            name="password"
            render={(field) => (
              <field.Container
                label={m.fresh_civil_platypus_talk()}
                disableController
              >
                <div className="relative">
                  <InputElement>
                    <LockIcon />
                  </InputElement>
                  <field.Controller>
                    <Input
                      type="password"
                      placeholder={m.fresh_civil_platypus_talk()}
                    />
                  </field.Controller>
                </div>
              </field.Container>
            )}
          />

          <form.Field
            name="consent"
            render={(field) => (
              <field.Container>
                <div className="flex items-center space-x-2">
                  <field.Controller>
                    <Checkbox
                      id={field.name}
                      checked={field.state.value}
                      onCheckedChange={(value) =>
                        field.handleChange(value === true)
                      }
                    />
                  </field.Controller>
                  <label
                    htmlFor={field.name}
                    className="text-sm text-gray-fg-secondary peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {m.simple_north_racoon_dig()}
                    <a href="/terms" className="text-accent-fg-primary">
                      {m.bright_house_shrimp_flop()}
                    </a>
                    {m.even_maroon_robin_blend()}
                    <a href="/privacy" className="text-accent-fg-primary">
                      {m.awake_inner_mole_coax()}
                    </a>
                  </label>
                </div>
              </field.Container>
            )}
          />

          <div className="pt-4">
            <form.Submit className="w-full">
              {m.nimble_candid_boar_transform()}
            </form.Submit>

            <div className="flex pt-2">
              <span className="text-sm text-gray-fg-secondary">
                {m.super_calm_bullock_lead()}
              </span>
              <a className="text-sm text-accent-fg-primary" href="/auth/login">
                {m.proud_white_millipede_comfort()}
              </a>
            </div>
          </div>

          <p className="text-xs text-gray-solid-hover">
            {m.lofty_petty_larva_earn()}
          </p>
        </form.Root>
      </CardContent>
    </Card>
  );
}
